import React, { useState } from 'react';
import {
    MenuDropdown,
    MenuDropdownItem,
    MenuDropdownProps,
} from '@instinet-analytics/semantic-ui-components';
import LayoutManagerModal from 'layoutManagement/LayoutManagerModal';
import CreateLayoutModal from 'layoutManagement/createLayout/CreateLayoutModal';
import {
    usePersistDirtyLayout,
    useSaveDebouncedLayoutChange,
    useToggleLayoutLocked,
} from 'layout/store/hooks';
import SaveLayoutAsModal from 'layoutManagement/createLayout/SaveLayoutAsModal';
import {
    useLayoutSelector,
    useSelectIsLayoutDirty,
    useSelectLayoutManualSave,
} from 'layout/store/selectors';
import {
    DropdownDivider,
    DropdownHeader,
} from '@instinet-analytics/semantic-ui-components';
import { useLayoutsList } from 'layout/store/useLayoutsList';
import ManagedLayout, {
    removeGlobalsWhenDuplicatedInUser,
} from 'layoutManagement/ManagedLayout';
import classNames from 'classnames';

import './LayoutsDropdown.scss';
import {
    useDispatchResetLayout,
    useDispatchSwitchLayoutAction,
} from 'layout/store/dispatcher';
import ShareLayoutModal from 'layoutManagement/shareLayout/ShareLayoutModal';
import { useAppLayoutOrigin } from 'layout/WellKnownLayoutOrigin';

export default function LayoutsDropdown(props: {
    layoutHeight: number;
    layoutWidth: number;
}) {
    const { isLocked, toggleLocked } = useToggleLayoutLocked();
    const activeLayout = useLayoutSelector();
    const appLayoutOrigin = useAppLayoutOrigin();
    const { layoutList } = useLayoutsList(appLayoutOrigin);
    const { layoutHeight, layoutWidth } = props;

    const manualSave = useSelectLayoutManualSave();
    const isDirty = useSelectIsLayoutDirty();
    const needToManualSave = manualSave && isDirty;

    const filteredLayouts = removeGlobalsWhenDuplicatedInUser(layoutList);

    const saveLayout = usePersistDirtyLayout();
    const saveDebouncedLayoutChange = useSaveDebouncedLayoutChange();

    const [isModalOpen, setModalOpen] = useState<
        'none' | 'create' | 'saveAs' | 'share' | 'manager'
    >('none');
    const isCreateLayoutModalOpen = isModalOpen === 'create';
    const isSaveAsLayoutModalOpen = isModalOpen === 'saveAs';
    const isShareLayoutModalOpen = isModalOpen === 'share';
    const isLayoutManagerModalOpen = isModalOpen === 'manager';
    const openModal = (modal: 'create' | 'saveAs' | 'share' | 'manager') => {
        saveDebouncedLayoutChange();
        setModalOpen(modal);
    };
    const closeModal = () => {
        setModalOpen('none');
    };

    const dispatchSwitchLayout = useDispatchSwitchLayoutAction();
    const dispatchResetLayout = useDispatchResetLayout();

    const lockDropdownOptionProps = isLocked
        ? { icon: 'lock', text: 'Unlock Layout' }
        : { icon: 'lock open', text: 'Lock Layout' };

    const availableHeight = layoutHeight - 300; // Allow enough for top menu, plus this components menu items above the switch layout section
    const switchPanelMaxHeight = Math.max(availableHeight, 200);
    const switchStyle = { maxHeight: switchPanelMaxHeight };

    const saveChanges = () => {
        saveLayout();
    };

    const discardChanges = () => {
        dispatchResetLayout();
        dispatchSwitchLayout(activeLayout!.origin, activeLayout!.name);
    };

    const align: MenuDropdownProps['alignDropdown'] | undefined =
        layoutWidth >= 250 ? 'right' : undefined;

    return (
        <>
            <MenuDropdown
                icon="block layout"
                text="Layouts"
                className="LayoutsDropdown"
                alignDropdown={align}
                maxWidth={layoutWidth}
            >
                <MenuDropdownItem
                    {...lockDropdownOptionProps}
                    onClick={toggleLocked}
                />
                {!needToManualSave && (
                    <>
                        <MenuDropdownItem
                            className="create-new-layout-menu-item"
                            text="Create New Layout"
                            icon="add"
                            onClick={() => openModal('create')}
                        />
                        {activeLayout && (
                            <>
                                <MenuDropdownItem
                                    className="save-layout-as-menu-item"
                                    text="Save As"
                                    icon="save"
                                    onClick={() => openModal('saveAs')}
                                />

                                <MenuDropdownItem
                                    className="share-layout-menu-item"
                                    text="Share"
                                    icon="share alternate"
                                    onClick={() => openModal('share')}
                                />
                            </>
                        )}
                    </>
                )}
                <MenuDropdownItem
                    text="Manage Layouts"
                    icon="list ul"
                    onClick={() => openModal('manager')}
                    className="ManageLayouts"
                />

                <DropdownDivider />
                {!needToManualSave && (
                    <>
                        <DropdownHeader>Switch Layout</DropdownHeader>
                        <div className="layouts-list" style={switchStyle}>
                            {filteredLayouts.map((l) => (
                                <SwitchLayoutMenuItem
                                    layout={l}
                                    key={`${l.layoutId}-${l.name}`}
                                />
                            ))}
                        </div>
                    </>
                )}
                {needToManualSave && (
                    <>
                        <MenuDropdownItem
                            className={'save-changes'}
                            text="Save Changes"
                            icon="save"
                            onClick={saveChanges}
                        />
                        <MenuDropdownItem
                            className={'discard-changes'}
                            text="Discard Changes"
                            icon="undo"
                            onClick={discardChanges}
                        />
                    </>
                )}
            </MenuDropdown>
            <CreateLayoutModal
                isOpen={isCreateLayoutModalOpen}
                onActionCompleted={closeModal}
            />
            {activeLayout ? (
                <>
                    <SaveLayoutAsModal
                        isOpen={isSaveAsLayoutModalOpen}
                        activeLayout={activeLayout}
                        onActionCompleted={closeModal}
                    />
                    <ShareLayoutModal
                        isOpen={isShareLayoutModalOpen}
                        activeLayout={activeLayout}
                        onActionCompleted={closeModal}
                    />
                </>
            ) : (
                <></>
            )}
            <LayoutManagerModal
                isOpen={isLayoutManagerModalOpen}
                onActionCompleted={closeModal}
            />
        </>
    );

    function SwitchLayoutMenuItem(props: { layout: ManagedLayout }) {
        const { layout } = props;

        const isActive = layout.layoutId === activeLayout?.id;
        const cssClasses = classNames(
            'SwitchLayoutMenuItem',
            isActive && 'active'
        );

        return (
            <MenuDropdownItem
                className={cssClasses}
                text={`${layout.name}${layout.isGlobal ? ' (Global)' : ''}${
                    isActive ? ' (Active)' : ''
                }`}
                onClick={() => {
                    if (!isActive) {
                        saveDebouncedLayoutChange();
                        dispatchSwitchLayout(activeLayout!.origin, layout.name);
                    }
                }}
            />
        );
    }
}
